import { Children, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import type { ReactNode } from 'react';

import IconButton from 'components/partials/button/icon-button';

import Icons from 'assets/icons';

export interface FunnelCardGridProps {
  children: ReactNode;
}

const SCROLL_THRESHOLD_TILE_COUNT = 5;

const SCROLLING_TILE_CLASSES =
  'grow-0 shrink-0 basis-[calc((100%-4*0.75rem)/4.5)]';

const SCROLL_END_TOLERANCE_PX = 1;

const getScrollIncrementPx = (row: HTMLDivElement): number => {
  const firstTile = row.firstElementChild as HTMLElement | null;

  if (!firstTile) return row.clientWidth;

  const tileWidth = firstTile.getBoundingClientRect().width;
  const gap = Number.parseFloat(getComputedStyle(row).columnGap || '0');

  return tileWidth + gap;
};

const FunnelCardGrid = ({ children }: FunnelCardGridProps) => {
  const hasScrollingLayout =
    Children.count(children) >= SCROLL_THRESHOLD_TILE_COUNT;
  const rowRef = useRef<HTMLDivElement>(null);
  const leftArrowRef = useRef<HTMLButtonElement>(null);
  const rightArrowRef = useRef<HTMLButtonElement>(null);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
  const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
  const isScrolling = hasScrollingLayout || isOverflowing;

  useEffect(() => {
    const row = rowRef.current;

    if (!row) return;

    const updateScrollability = (): void => {
      const overflowPx = row.scrollWidth - row.clientWidth;
      const nextCanScrollLeft = row.scrollLeft > SCROLL_END_TOLERANCE_PX;
      const nextCanScrollRight =
        overflowPx - row.scrollLeft > SCROLL_END_TOLERANCE_PX;

      if (
        !nextCanScrollLeft &&
        document.activeElement === leftArrowRef.current
      ) {
        rowRef.current?.focus();
      }

      if (
        !nextCanScrollRight &&
        document.activeElement === rightArrowRef.current
      ) {
        rowRef.current?.focus();
      }

      setIsOverflowing(overflowPx > SCROLL_END_TOLERANCE_PX);
      setCanScrollLeft(nextCanScrollLeft);
      setCanScrollRight(nextCanScrollRight);
    };

    updateScrollability();

    row.addEventListener('scroll', updateScrollability);
    window.addEventListener('resize', updateScrollability);

    const resizeObserver = new ResizeObserver(updateScrollability);

    resizeObserver.observe(row);

    return () => {
      row.removeEventListener('scroll', updateScrollability);
      window.removeEventListener('resize', updateScrollability);
      resizeObserver.disconnect();
    };
  }, [children]);

  const handleScrollLeft = (): void => {
    const row = rowRef.current;

    if (!row) return;

    row.scrollBy({ left: -getScrollIncrementPx(row), behavior: 'smooth' });
  };

  const handleScrollRight = (): void => {
    const row = rowRef.current;

    if (!row) return;

    row.scrollBy({ left: getScrollIncrementPx(row), behavior: 'smooth' });
  };

  const row = (
    <div
      ref={rowRef}
      className="flex min-w-0 gap-3 overflow-x-auto pb-2 scrollbar-thin"
      {...(isScrolling && {
        tabIndex: 0,
        role: 'region',
        'aria-label': 'Funnel stages',
      })}
    >
      {Children.map(children, (child) => (
        <div
          className={classNames(
            'flex min-w-[200px]',
            hasScrollingLayout ? SCROLLING_TILE_CLASSES : 'flex-1 basis-0'
          )}
        >
          {child}
        </div>
      ))}
    </div>
  );

  return (
    <div className="relative min-w-0">
      {row}

      {isScrolling && (
        <>
          <div
            data-testid="funnel-card-grid-fade-left"
            aria-hidden="true"
            className={classNames(
              'absolute inset-y-0 left-0 z-[5] w-16 pointer-events-none bg-gradient-to-r from-white/90 to-white/0',
              !canScrollLeft && 'invisible'
            )}
          />

          <div
            data-testid="funnel-card-grid-fade-right"
            aria-hidden="true"
            className={classNames(
              'absolute inset-y-0 right-0 z-[5] w-16 pointer-events-none bg-gradient-to-l from-white/90 to-white/0',
              !canScrollRight && 'invisible'
            )}
          />

          <IconButton
            ref={leftArrowRef}
            variant="reserve-outlined"
            size="xs"
            icon={<Icons.ChevronLeft aria-hidden="true" />}
            aria-label="Scroll funnel stages left"
            onClick={handleScrollLeft}
            aria-hidden={!canScrollLeft || undefined}
            tabIndex={canScrollLeft ? undefined : -1}
            className={classNames(
              'absolute left-3 top-1/2 z-10 -translate-y-1/2 shadow-sm',
              !canScrollLeft && 'invisible'
            )}
          />

          <IconButton
            ref={rightArrowRef}
            variant="reserve-outlined"
            size="xs"
            icon={<Icons.ChevronRight aria-hidden="true" />}
            aria-label="Scroll funnel stages right"
            onClick={handleScrollRight}
            aria-hidden={!canScrollRight || undefined}
            tabIndex={canScrollRight ? undefined : -1}
            className={classNames(
              'absolute right-3 top-1/2 z-10 -translate-y-1/2 shadow-sm',
              !canScrollRight && 'invisible'
            )}
          />
        </>
      )}
    </div>
  );
};

export default FunnelCardGrid;
